import { useEffect, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DOCUMENT_TYPE_BY_CODE, SITE_DOCUMENT_TYPES } from '@erp-attach/shared';
import type { EntityType } from '@erp-attach/shared';
import { useApi } from '../api/ApiProvider';
import type { FolderNode, SearchResult } from '../api/types';
import { entityRoute } from '../lib/links';
import { stashPendingUpload } from '../lib/pendingUpload';
import { formatBytes } from '../lib/format';
import { Button } from './ui/Button';
import { Dialog } from './ui/Dialog';
import { ErrorBanner } from './ui/ErrorBanner';
import { Icon } from './ui/Icon';
import { Spinner } from './ui/Spinner';
import { FilePlate } from './FilePlate';
import { KIND_ICON, KIND_LABEL } from './GlobalSearch';
import { visibleFolders } from './FolderTree';
import { SearchScopeToggle } from './SearchScopeToggle';
import { filterByScope, useSearchScope } from '../lib/searchScope';

export interface UploadDestinationDialogProps {
  open: boolean;
  files: File[];
  onClose: () => void;
}

/** A search hit that is a record (never a document). */
type RecordHit = SearchResult & { kind: EntityType };

function isRecordHit(r: SearchResult): r is RecordHit {
  return r.kind !== 'document';
}

interface Target {
  folder: FolderNode;
  depth: number;
  types: string[];
}

/** Every folder the caller may upload into, flattened with its accepted types. */
function uploadTargets(folders: readonly FolderNode[], entityType: EntityType): Target[] {
  const out: Target[] = [];
  const walk = (list: readonly FolderNode[], depth: number) => {
    for (const f of list) {
      const types = f.documentTypes.length > 0 ? f.documentTypes : f.siteId ? [...SITE_DOCUMENT_TYPES] : [];
      if (types.length > 0 && f.actions.includes('upload') && f.status !== 'archived') out.push({ folder: f, depth, types });
      walk(f.children, depth + 1);
    }
  };
  walk(visibleFolders(folders, entityType), 0);
  return out;
}

/**
 * Upload-first flow: the files are already chosen; this picks the record, then the folder, and
 * hands the files to that folder's queue on the record page.
 */
export function UploadDestinationDialog({ open, files, onClose }: UploadDestinationDialogProps) {
  const api = useApi();
  const navigate = useNavigate();
  const inputId = useId();
  const [q, setQ] = useState('');
  const [scope, setScope] = useSearchScope();
  const [hits, setHits] = useState<RecordHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [record, setRecord] = useState<RecordHit | null>(null);
  const [targets, setTargets] = useState<Target[] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!open) {
      setQ('');
      setHits([]);
      setRecord(null);
      setTargets(null);
      setError(null);
    }
  }, [open]);

  // Record search (entities only).
  useEffect(() => {
    const needle = q.trim();
    if (needle.length < 2 || record) {
      setHits([]);
      return;
    }
    const controller = new AbortController();
    const t = window.setTimeout(() => {
      setSearching(true);
      api
        .search(needle, controller.signal)
        .then((res) => {
          if (controller.signal.aborted) return;
          setHits(res.results.filter(isRecordHit).slice(0, 6));
          setSearching(false);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          setError(err instanceof Error ? err : new Error(String(err)));
          setSearching(false);
        });
    }, 220);
    return () => {
      window.clearTimeout(t);
      controller.abort();
    };
  }, [api, q, record]);

  // Folders of the chosen record.
  useEffect(() => {
    if (!record) return;
    const controller = new AbortController();
    setTargets(null);
    setError(null);
    const load =
      record.kind === 'customer'
        ? api.customers.get(record.number, controller.signal)
        : record.kind === 'site'
          ? api.jobs.get(record.number, controller.signal)
          : api.suppliers.get(record.number, controller.signal);
    load
      .then((res) => {
        if (controller.signal.aborted) return;
        if (res.status !== 'ready') {
          setError(new Error('This record is still being provisioned. Try again in a few minutes.'));
          setTargets([]);
          return;
        }
        setTargets(uploadTargets(res.folders, record.kind));
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setTargets([]);
      });
    return () => controller.abort();
  }, [api, record]);

  const totalBytes = useMemo(() => files.reduce((n, f) => n + f.size, 0), [files]);

  const choose = (t: Target) => {
    if (!record) return;
    stashPendingUpload(t.folder.id, files);
    onClose();
    navigate(`${entityRoute(record.kind, record.number)}?folder=${encodeURIComponent(t.folder.id)}`);
  };

  return (
    <Dialog open={open} title={record ? `Where in ${record.title}?` : 'Where do these files go?'} onClose={onClose}>
      <ul className="mb-4 max-h-32 space-y-1 overflow-auto rounded-lg border border-border bg-surface-raised p-2" aria-label="Files to upload">
        {files.map((f, i) => (
          <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-xs">
            <FilePlate filename={f.name} mime={f.type} className="!h-7 !w-7 !text-[0.6rem]" />
            <span className="min-w-0 flex-1 truncate">{f.name}</span>
            <span className="gsl-num text-muted-foreground">{formatBytes(f.size)}</span>
          </li>
        ))}
        {files.length > 1 ? (
          <li className="gsl-num pt-1 text-right text-xs text-muted-foreground">
            {files.length} files, {formatBytes(totalBytes)}
          </li>
        ) : null}
      </ul>

      {!record ? (
        <>
          <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
            <label htmlFor={inputId} className="text-xs font-medium text-muted-foreground">
              Customer, job or supplier
            </label>
            <SearchScopeToggle value={scope} onChange={setScope} size="sm" />
          </div>
          <div className="relative mt-1">
            <Icon name="search" size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id={inputId}
              className="gsl-input pl-8"
              placeholder="Name, account number or job reference"
              value={q}
              autoFocus
              autoComplete="off"
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="mt-2 min-h-[6rem]">
            {searching && hits.length === 0 ? (
              <div className="py-3">
                <Spinner label="Searching" />
              </div>
            ) : filterByScope(hits, scope).length > 0 ? (
              <ul className="divide-y divide-border rounded-lg border border-border">
                {filterByScope(hits, scope).map((r) => (
                  <li key={`${r.kind}-${r.id}`}>
                    <button type="button" className="gsl-search-item" onClick={() => setRecord(r)}>
                      <Icon name={KIND_ICON[r.kind]} size={16} className="shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{r.title}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {KIND_LABEL[r.kind]} <span className="gsl-num">{r.number}</span>
                          {r.subtitle ? ` · ${r.subtitle}` : ''}
                        </span>
                      </span>
                      <Icon name="chevron-right" size={15} className="text-muted-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : q.trim().length >= 2 && !searching ? (
              <p className="py-3 text-sm text-muted-foreground">No records match “{q.trim()}”.</p>
            ) : (
              <p className="py-3 text-sm text-muted-foreground">Start typing to find the record these files belong to.</p>
            )}
          </div>
        </>
      ) : (
        <>
          <button type="button" className="gsl-link mb-2 inline-flex items-center gap-1 text-xs" onClick={() => setRecord(null)}>
            <Icon name="chevron-left" size={13} /> Pick a different record
          </button>
          {targets === null ? (
            <div className="py-3">
              <Spinner label="Loading folders" />
            </div>
          ) : targets.length === 0 ? (
            <p className="py-3 text-sm text-muted-foreground">You cannot upload to any folder on this record.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border" aria-label="Folders">
              {targets.map((t) => (
                <li key={t.folder.id}>
                  <button type="button" className="gsl-search-item" onClick={() => choose(t)} style={{ paddingLeft: `${0.85 + t.depth * 0.9}rem` }}>
                    <Icon name="folder" size={16} className="shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{t.folder.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {t.types.map((c) => DOCUMENT_TYPE_BY_CODE.get(c)?.label ?? c).join(', ')}
                      </span>
                    </span>
                    <Icon name="chevron-right" size={15} className="text-muted-foreground" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {error ? (
        <div className="mt-3">
          <ErrorBanner error={error} />
        </div>
      ) : null}
      <div className="mt-4 flex justify-end">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Dialog>
  );
}
